// /rfc — a standalone RFP micro-site, its own app entirely (frontend-src/rfc/),
// deployed as a separate Railway service from the main Metergy frontend. Own
// login (RFC_USERNAME/RFC_PASSWORD on the backend, via backend/rfc_auth.py).
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Lock, AlertCircle, LogIn, Eye, EyeOff, LogOut } from "lucide-react";
import RfcCopilot from "./RfcCopilot";
import metergyLogo from "./assets/metergy-logo.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const DOC_ID = "metergy-bgo-rfp-proposal-html";
const DEMO_VIDEO = "nate-ai-demo.mp4";
const TOKEN_KEY = "rfc_token";

function RfcLogin({ onLoggedIn }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/rfc/login`, { username: username.trim(), password });
      localStorage.setItem(TOKEN_KEY, res.data.access_token);
      onLoggedIn(res.data.access_token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#eef2f4] px-4" data-testid="rfc-login">
      <div className="w-full max-w-sm rounded-2xl border border-[#c7d5db] bg-white p-8 shadow-sm">
        <div className="mb-5 flex items-center justify-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#7fc242] to-[#5aa033]">
            <span className="text-sm font-black text-white">B</span>
          </div>
          <span className="text-sm font-bold tracking-tight text-[#0d1b26]">Bill Gosling Outsourcing</span>
          <span className="text-base font-light text-[#c7d5db]">×</span>
          <img src={metergyLogo} alt="Metergy Solutions" className="h-8 w-8 rounded-full" />
        </div>

        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0f7fa1]">
            <Lock className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-lg font-bold text-[#0d1b26]">RFP Access</div>
            <div className="text-xs text-[#6f8592]">Metergy Solutions proposal</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3.5">
          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-[#e2b6b0] bg-[#fbeceb] px-3 py-2.5 text-sm text-[#b3261e]">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}
          <input
            type="text"
            autoComplete="username"
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Username"
            data-testid="rfc-username"
            className="w-full rounded-lg border border-[#c7d5db] bg-[#fbfcfd] px-3.5 py-2.5 text-sm text-[#0d1b26] placeholder-[#6f8592] outline-none transition focus:border-[#0f7fa1] focus:ring-2 focus:ring-[#0f7fa1]/20"
          />
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="rfc-password"
              className="w-full rounded-lg border border-[#c7d5db] bg-[#fbfcfd] px-3.5 py-2.5 pr-10 text-sm text-[#0d1b26] placeholder-[#6f8592] outline-none transition focus:border-[#0f7fa1] focus:ring-2 focus:ring-[#0f7fa1]/20"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6f8592] hover:text-[#0d1b26]"
            >
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="rfc-login-btn"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#0f7fa1] py-2.5 text-sm font-bold text-white transition hover:bg-[#0b5f78] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : <><LogIn className="h-4 w-4" /> Sign in</>}
          </button>
        </form>
      </div>
    </div>
  );
}

function RfcContent({ token, onLogout }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState("");
  const frameRef = useRef(null);
  const [frameReady, setFrameReady] = useState(false);

  useEffect(() => {
    let objectUrl;
    axios
      .get(`${API}/rfc/docs/${DOC_ID}/download`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          onLogout();
          return;
        }
        setError("Couldn't load the page. Is the backend running?");
      });
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [token, onLogout]);

  // The proposal page embeds the Nate AI demo video. It renders from a blob URL, so
  // a <video src> inside it cannot carry the bearer token; fetch the file here with
  // auth once the page has loaded and post the blob URL into the iframe. Blob URLs
  // are scoped to this origin, which the blob-sourced iframe shares.
  useEffect(() => {
    if (!frameReady || !url) return undefined;
    let videoUrl;
    let cancelled = false;
    const frame = frameRef.current;
    axios
      .get(`${API}/rfc/media/${DEMO_VIDEO}`, { responseType: "blob", headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (cancelled || !frame?.contentWindow) return;
        videoUrl = URL.createObjectURL(res.data);
        frame.contentWindow.postMessage({ type: "demo-video", url: videoUrl }, "*");
      })
      .catch(() => {
        if (!cancelled && frame?.contentWindow) {
          frame.contentWindow.postMessage({ type: "demo-video-unavailable" }, "*");
        }
      });
    return () => {
      cancelled = true;
      if (videoUrl) URL.revokeObjectURL(videoUrl);
    };
  }, [frameReady, url, token]);

  return (
    <div className="flex h-screen flex-col" style={{ background: "#eef2f4" }} data-testid="rfc-content">
      <div className="flex items-center justify-end border-b border-[#c7d5db] bg-white px-4 py-1.5">
        <button
          onClick={onLogout}
          data-testid="rfc-logout"
          className="flex items-center gap-1.5 text-xs text-[#435a68] transition hover:text-[#0d1b26]"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
      <main className="flex-1">
        {error ? (
          <div className="flex h-full items-center justify-center text-sm text-[#b3261e]">{error}</div>
        ) : url ? (
          <iframe ref={frameRef} title="Metergy RFP Proposal" src={url} onLoad={() => setFrameReady(true)} className="h-full w-full border-0 bg-white" />
        ) : (
          <div className="flex h-full items-center justify-center gap-2 text-[#6f8592]">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        )}
      </main>
      <RfcCopilot token={token} />
    </div>
  );
}

export default function RfcPortal() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  const handleLogout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  }, []);

  return token ? (
    <RfcContent token={token} onLogout={handleLogout} />
  ) : (
    <RfcLogin onLoggedIn={setToken} />
  );
}
